export type DurationBadge = {
  id: string
  label: string
  days: number
}

export type DurationBadgeConfig = {
  id: string
  days: number
}

export const createDurationBadges = (
  configs: DurationBadgeConfig[],
  translationFn: (key: string) => string,
  translationPrefix: string = 'durationBadge'
): DurationBadge[] => {
  return configs.map((config) => ({
    id: config.id,
    label: translationFn(`${translationPrefix}${config.id}`),
    days: config.days,
  }))
}

export const findMatchingBadge = (
  duration: number,
  configs: DurationBadgeConfig[],
  hasEndDate: boolean,
  isCurrently: boolean
): string | null => {
  if (isCurrently || !hasEndDate) {
    return null
  }

  const matchingBadge = configs.find((badge) => {
    // Special handling for specific badges that might need exact matches
    if (badge.id === '6months') {
      return duration === 182 || duration === 183
    } else if (badge.id === '1year') {
      return duration === 365 || duration === 366
    } else {
      // For other badges, check if duration matches days + 1
      return duration === badge.days + 1
    }
  })

  return matchingBadge ? matchingBadge.id : null
}

export const calculateDuration = (
  startDate?: Date | null,
  endDate?: Date | null,
  currently?: boolean
): number => {
  if (!startDate) return 0

  if (currently) {
    const start = new Date(startDate)
    const end = new Date()

    const startNormalized = new Date(
      Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
    )
    const endNormalized = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()))

    if (endNormalized < startNormalized) {
      return 0
    }

    const diffTime = endNormalized.getTime() - startNormalized.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    return diffDays + 1
  }

  if (!endDate) return 0

  const start = new Date(startDate)
  const end = new Date(endDate)

  const startNormalized = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()))
  const endNormalized = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()))

  if (endNormalized < startNormalized) {
    return 0
  }

  const diffTime = endNormalized.getTime() - startNormalized.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  return diffDays + 1
}

export const calculateEndDateFromBadge = (startDate: Date, days: number): Date => {
  const start = new Date(startDate)

  const normalizedStart = new Date(
    Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 12, 0, 0, 0)
  )

  const endDate = new Date(normalizedStart)
  endDate.setUTCDate(endDate.getUTCDate() + days)

  return endDate
}

export const calculateEndDateFromBadgeExact = (startDate: Date, days: number): Date => {
  const start = new Date(startDate)

  const normalizedStart = new Date(
    Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 12, 0, 0, 0)
  )

  const endDate = new Date(normalizedStart)
  endDate.setUTCDate(endDate.getUTCDate() + days - 1)

  return endDate
}

export const getDurationText = (
  duration: number,
  hasStartDate: boolean,
  currently: boolean,
  translationFn: (key: string, options?: any) => string,
  context: 'treatment' | 'training' = 'treatment'
): string => {
  const prefix = context === 'treatment' ? 'treatmentDuration' : 'trainingDuration'

  if (!hasStartDate) {
    return currently
      ? translationFn(`${prefix}TextCurrentlyNoStartDate`)
      : translationFn(`${prefix}TextNoDate`)
  }

  if (currently) {
    return translationFn(`${prefix}TextCurrently`)
  }

  if (duration === 0) {
    return translationFn(`${prefix}TextZero`)
  }

  if (duration === 1) {
    return translationFn(`${prefix}TextSingle`)
  }

  return translationFn(`${prefix}TextPlural`, { days: duration })
}

export const TREATMENT_DURATION_BADGES: DurationBadgeConfig[] = [
  { id: '3days', days: 2 }, // Add 2 days for 3-day duration
  { id: '1week', days: 6 }, // Add 6 days for 7-day duration
  { id: '1month', days: 29 }, // Add 29 days for 30-day duration
  { id: '6months', days: 182 }, // Add 182 days for 183-day duration
  { id: '1year', days: 364 }, // Add 364 days for 365-day duration
]

export const TRAINING_DURATION_BADGES: DurationBadgeConfig[] = [
  { id: '1week', days: 6 }, // Add 6 days for 7-day duration
  { id: '2weeks', days: 13 }, // Add 13 days for 14-day duration
  { id: '1month', days: 29 }, // Add 29 days for 30-day duration
  { id: '3months', days: 89 }, // Add 89 days for 90-day duration
]
